import {
  Button,
  CloseButton,
  Flex,
  Heading,
  IconDownloadLine,
  IconTrashLine,
  Spinner,
  Table,
  Text,
  Tooltip,
  Tray,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AssessmentPlanService } from "../../../services/AssessmentService";
import { Files } from "../../../services/FilesService";
import { UserDetailContext } from "../../../utils/Context";
import downloadFile from "../../../utils/Dom";
import { waitForExpectedResult } from "../../../utils/Timer";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import EnrichedFileDrop from "../../LearningOutcomes/FileDrop/EnrichedFileDrop";
import { emailMatches, evidenceFileTypes } from "./utils";

const UploadEvidenceModal = ({ open, planId, assessment, handleCancel }) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [fileState, setFileState] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentFiles, setCurrentFiles] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  useEffect(() => {
    if (open && !currentFiles) {
      handleReviewEvidence(assessment, 0);
    }
  }, [open]);

  const dateTimeDifference = (currentFiles, filesList) => {
    for (let i = 0; i < currentFiles.length; i++) {
      if (currentFiles[i].last_modified !== filesList[i].last_modified) {
        return true;
      }
    }
    return false;
  };

  const handleReviewEvidence = async (assessment, newEvidenceCount) => {
    if (newEvidenceCount > 0 && currentFiles) {
      setDownloading(true);
      waitForExpectedResult(
        () => Files.reviewFiles("evidence", assessment.id),
        (filesList) => {
          return (
            filesList.length !== currentFiles.length ||
            dateTimeDifference(currentFiles, filesList)
          );
        },
        (files) => {
          setCurrentFiles(files);
          setDownloading(false);
        },
        () => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("evidence.evidence"),
            }),
            ERROR
          );
          setDownloading(false);
        },
        30,
        3000
      );
    } else {
      await Files.reviewFiles("evidence", assessment.id)
        .then((files) => setCurrentFiles(files))
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("evidence.evidence"),
            }),
            ERROR
          )
        )
        .finally(() => setDownloading(false));
    }
  };

  const handleSubmitEvidenceUpload = (evidenceFiles, assessment) => {
    setUploading(true);
    Files.saveFiles(evidenceFiles, "evidence", assessment.id, null, false)
      .then(() => {
        setFileState(null);
        handleReviewEvidence(assessment, evidenceFiles.length);
        showNotification(t("juriedAssessment.uploadEvidenceSuccess"));
      })
      .catch(() =>
        showNotification(t("notifications.general.uploadError"), ERROR)
      )
      .finally(() => setUploading(false));
  };

  const handleApproveEvidence = async () => {
    await AssessmentPlanService.approveAssessmentEvidence(planId, assessment.id)
      .then((res) => {
        assessment.evidence_approved_on = res.evidence_approved_on;
        showNotification(t("notifications.evidenceApproval.approved"));
        setApproveDialogOpen(false);
        handleCancel();
      })
      .catch(() =>
        showNotification(
          t("notifications.evidenceApproval.approvedError"),
          ERROR
        )
      );
  };

  const handleDownloadAllEvidence = () => {
    setDownloadingAll(true);
    Files.downloadFiles("evidence", assessment.id)
      .catch(() => showNotification(t("notifications.general.downloadError")))
      .finally(() => setDownloadingAll(false));
  };

  const DeleteFileButton = ({ file }) => {
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleDeleteEvidence = (file_name) => {
      setDeleteLoading(true);
      Files.deleteFile(file_name, "evidence", assessment.id)
        .then(() =>
          setCurrentFiles((prevFiles) =>
            prevFiles.filter((file) => file.file_name !== file_name)
          )
        )
        .catch(() =>
          showNotification(
            t("notifications.general.deleteError", {
              obj: t("evidence.evidence"),
            }),
            ERROR
          )
        )
        .finally(() => setDeleteLoading(false));
    };

    return (
      <Tooltip renderTip={t("evidence.delete")} color="primary-inverse">
        <Button
          renderIcon={
            deleteLoading ? (
              <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
            ) : (
              IconTrashLine
            )
          }
          disabled={deleteLoading}
          margin="0 0 0 small"
          size="small"
          onClick={() => handleDeleteEvidence(file.file_name)}
        />
      </Tooltip>
    );
  };

  return (
    <>
      <Tray
        size="regular"
        label={t("assessment.uploadDialog")}
        open={open}
        placement="end"
        onClose={() => setFileState(null)}
        themeOverride={{ zIndex: 9998 }}
      >
        <Flex padding="small" direction="column" height="100vh">
          <Flex.Item margin="0 0 small 0">
            <Heading level="h2" as="h1" border="bottom">
              {t("assessment.manageEvidence")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              placement="end"
              offset="small"
              screenReaderLabel={t("global.close")}
              onClick={handleCancel}
            />
          </Flex.Item>
          {!assessment.evidence_approved_on && (
            <Flex.Item padding="xx-small">
              <EnrichedFileDrop
                fileState={fileState}
                setFileState={setFileState}
                fileDropHeading={t("assessment.dropEvidence")}
                fileExtension={evidenceFileTypes}
                multiFileSupport={true}
              />
            </Flex.Item>
          )}
          {!assessment.evidence_approved_on && (
            <Flex.Item padding="xx-small">
              <Flex alignItems="start" justifyItems="space-between">
                <Flex.Item>
                  <Text size="small">
                    {t("juriedAssessment.uploadInstructions")}
                  </Text>
                </Flex.Item>
                <Flex.Item>
                  <Button
                    onClick={() =>
                      handleSubmitEvidenceUpload(fileState, assessment)
                    }
                    color="primary"
                    disabled={!fileState || uploading}
                  >
                    {uploading ? (
                      <Spinner
                        renderTitle={t("global.pleaseWait")}
                        size="x-small"
                      />
                    ) : (
                      t("global.submit")
                    )}
                  </Button>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          )}
          {downloading && (
            <Flex.Item>
              <Skeleton height={45} count={5} style={{ marginBottom: 5 }} />
            </Flex.Item>
          )}
          {!downloading && currentFiles?.length > 0 && (
            <Flex.Item>
              <Text weight="bold">
                {assessment.evidence_approved_on
                  ? t("assessment.approvedEvidenceList")
                  : t("assessment.currentEvidenceList")}
                :
              </Text>
            </Flex.Item>
          )}
          {!downloading && currentFiles?.length > 0 && (
            <Flex.Item shouldShrink>
              <Table caption={t("assessment.currentEvidenceList")}>
                <Table.Body>
                  {currentFiles.map((file, key) => (
                    <Table.Row key={key}>
                      <Table.Cell>
                        <Text wrap="break-word">{file.file_name}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Flex justifyItems="end">
                          <Flex.Item>
                            <Tooltip
                              renderTip={t("global.download")}
                              color="primary-inverse"
                            >
                              <Button
                                renderIcon={IconDownloadLine}
                                size="small"
                                onClick={() =>
                                  downloadFile(file.file_url, file.file_name)
                                }
                              />
                            </Tooltip>
                          </Flex.Item>
                          <Flex.Item>
                            {!assessment.evidence_approved_on && (
                              <DeleteFileButton file={file} />
                            )}
                          </Flex.Item>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Flex.Item>
          )}
          {!downloading && currentFiles?.length > 0 && (
            <Flex.Item padding="xx-small">
              <Flex justifyItems="space-between">
                <Flex.Item>
                  <Button
                    margin="small 0 0 0"
                    renderIcon={
                      downloadingAll ? (
                        <Spinner
                          size="x-small"
                          renderTitle={t("global.pleaseWait")}
                        />
                      ) : (
                        IconDownloadLine
                      )
                    }
                    disabled={downloadingAll}
                    onClick={handleDownloadAllEvidence}
                  >
                    {t("juriedAssessment.downloadAllEvidence")}
                  </Button>
                </Flex.Item>
                {emailMatches(
                  userDetails.email,
                  assessment.evidence_approver
                ) &&
                  !assessment.evidence_approved_on && (
                    <Flex.Item>
                      <Button
                        color="primary"
                        margin="small 0 0 small"
                        onClick={() => setApproveDialogOpen(true)}
                      >
                        {t("analyze.approve")}
                      </Button>
                    </Flex.Item>
                  )}
              </Flex>
            </Flex.Item>
          )}
        </Flex>
      </Tray>
      <DialogYesNo
        message={t("assessment.approveConfirm")}
        openStatus={approveDialogOpen}
        onClickNo={() => setApproveDialogOpen(false)}
        onClickYes={handleApproveEvidence}
      />
    </>
  );
};

export default UploadEvidenceModal;
